import { useQuery } from '@tanstack/react-query';
import { InterviewerService } from '../InterviewerService';

// `enabled` lets a caller defer the request (e.g. the two non-default tabs on
// UpcomingInterviews only fetch once the interviewer actually switches to
// them, instead of firing all three status queries on page load).
// search/fromDatetime/toDatetime are server-side filters (the backend's own
// route takes them directly) — included in the queryKey so a change refetches
// rather than silently reusing a differently-filtered cache entry.
export const useInterviewerInterviews = (
  status,
  {
    page = 1,
    pageSize = 10,
    search,
    fromDatetime,
    toDatetime,
    enabled = true,
  } = {}
) =>
  useQuery({
    queryKey: [
      'interviewer',
      'interviews',
      status,
      page,
      pageSize,
      search,
      fromDatetime,
      toDatetime,
    ],
    queryFn: () =>
      InterviewerService.listInterviews(status, page, pageSize, {
        search,
        fromDatetime,
        toDatetime,
      }),
    enabled: !!status && enabled,
    select: (response) =>
      response?.data ?? { interviews: [], pagination: null },
  });

// Requirement + candidate + match + interview + timeline + scorecards +
// sibling-interview list for one interview_round_detail_id — the Interview
// Brief page's main data source.
export const useInterviewBrief = (interviewRoundDetailId) =>
  useQuery({
    queryKey: ['interviewer', 'interview-brief', interviewRoundDetailId],
    queryFn: () => InterviewerService.getBrief(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId,
    select: (response) => response?.data,
  });

// The generated verify/clarify question bank for that same interview.
export const useInterviewQuestionnaire = (interviewRoundDetailId) =>
  useQuery({
    queryKey: [
      'interviewer',
      'interview-questionnaire',
      interviewRoundDetailId,
    ],
    queryFn: () => InterviewerService.getQuestionnaire(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId,
    select: (response) => response?.data,
  });

// The interviewer's own private sticky notes for this interview. The API
// returns `data.notes` as an array (one entry per interviewer who has ever
// left a note here), but this widget only ever shows/edits the caller's
// own note, which is always the first entry — so unwrap straight to
// `{ comments }` here rather than threading the array shape through the
// component. `enabled` lets the Interview Brief page defer the fetch until
// the notes widget is actually opened, same gating this file's other
// on-visit queries already use.
export const useInterviewNotes = (interviewRoundDetailId, enabled = true) =>
  useQuery({
    queryKey: ['interviewer', 'interview-notes', interviewRoundDetailId],
    queryFn: () => InterviewerService.getNotes(interviewRoundDetailId),
    enabled: !!interviewRoundDetailId && enabled,
    select: (response) => response?.data?.notes?.[0] || null,
  });
